"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useGetAttributesAsColumns = void 0;
const react_1 = require("react");
const graphql_1 = require("../../../../../../../__generated__/graphql");
const constants_1 = require("../../hooks/constants");
const attributes_1 = require("../../../../../../client/attributes");
const client_constants_1 = require("../../../../../../client/client.constants");
const measures_1 = require("../../../../../../client/measures");
const table_1 = require("../../../../../../client/table");
const table_columns_1 = require("../../../../../../common/table-columns");
const product_table_dynamic_columns_1 = require("../../../../../../hooks/product-table-dynamic-columns");
const models_1 = require("../../../../../../models");
const utils_1 = require("../../../../../../utils");
const feature_flags_1 = require("../../../../../../../feature-flags");
const hooks_1 = require("../../../../../../../hooks");
/**
 * Hook: useGetAttributesAsColumns
 *
 * Description:
 * Fetches column/attribute data from three separate data sources:
 *  - original product attributes (via aggregated labels)
 *  - enriched/strategy attributes (via strategy attributes)
 *  - product measures
 *
 * The hook combines results from these endpoints, deduplicates entries
 * (keeping a single attribute entry per label), and enforces the final
 * ordering (attributes first, then measures, sorted case-insensitively).
 * It also merges pagination/loading/error state across the three sources
 * and exposes a single `pagination` object and `isLoading`/`error` fields
 * for consumers.
 *
 * Returns:
 *  - isLoading: boolean
 *  - pagination: { hasNextPage, isLoadingMore, onNextPage }
 *  - error: Error | undefined
 *  - attributes: TableColumnAttribute[]
 */
const useGetAttributesAsColumns = ({ catalogId, productTreeNodeId, productAttributeFilter, productMeasureFilter, sortingItem = models_1.SortingItem.SortByTextAsc, options, filterOptions, }) => {
    const { featureFlagsService } = (0, hooks_1.useFeatureFlagsService)();
    const strategyProductAttributeFilter = (0, react_1.useMemo)(() => (productAttributeFilter && {
        some: {
            label: productAttributeFilter.label,
        },
    }), [productAttributeFilter]);
    const { data: attributesInStrategyData, isLoading: attributesInStrategyLoading, error: attributesInStrategyError, pagination: { isLoadingMore: attributesInStrategyMoreItemsLoading, hasNextPage: attributesInStrategyHasNextPage, onNextPage: fetchMoreAttributesInStrategy, }, } = (0, attributes_1.useGetStrategyAttributes)({
        productTreeNodeId,
        sortingItem,
        pageSize: client_constants_1.REQUEST_LIMIT_MEDIUM,
        filter: strategyProductAttributeFilter,
        options,
    });
    const productAttributesFilter = (0, react_1.useMemo)(() => ({
        attribute: Object.assign({}, productAttributeFilter === null || productAttributeFilter === void 0 ? void 0 : productAttributeFilter.label),
        origin: {
            in: [graphql_1.ProductAttributeOrigin.Original],
        },
        productTreeNodeId,
        type: {
            equals: graphql_1.AttributeType.Label,
        },
    }), [productAttributeFilter === null || productAttributeFilter === void 0 ? void 0 : productAttributeFilter.label, productTreeNodeId]);
    const { data: productAggregateLabelsData, isLoading: productAggregateLabelsLoading, error: productAggregateLabelsError, pagination: { isLoadingMore: productAggregateLabelsMoreItemsLoading, hasNextPage: productLabelsHasNextPage, onNextPage: fetchMoreProductAggregateLabels, }, } = (0, table_1.useGetCatalogAttributions)({
        catalogId,
        pageSize: client_constants_1.REQUEST_LIMIT_MEDIUM,
        options,
        filter: productAttributesFilter,
    });
    const { data: productMeasuresData, isLoading: productMeasuresLoading, error: productMeasuresError, pagination: { isLoadingMore: productMeasuresMoreItemsLoading, onNextPage: fetchMoreProductMeasures, hasNextPage: productMeasuresHasNextPage, }, } = (0, measures_1.useGetAggregatedProductMeasures)({
        catalogId,
        productTreeNodeId,
        sortingItem,
        filter: productMeasureFilter,
        pageSize: client_constants_1.REQUEST_LIMIT_MEDIUM,
        options: Object.assign(Object.assign({}, options), { skip: !featureFlagsService.isFeatureEnabled(feature_flags_1.FeatureFlag.Measures) || (options === null || options === void 0 ? void 0 : options.skip) }),
    });
    const [pageNumber, setPageNumber] = (0, react_1.useState)(1);
    const hasNextPage = productLabelsHasNextPage || attributesInStrategyHasNextPage || productMeasuresHasNextPage;
    (0, react_1.useEffect)(() => {
        setPageNumber(1);
    }, [productTreeNodeId]);
    const onLoadMore = (0, react_1.useCallback)(() => __awaiter(void 0, void 0, void 0, function* () {
        if (!hasNextPage) {
            return;
        }
        const promises = [];
        if (productLabelsHasNextPage) {
            promises.push(fetchMoreProductAggregateLabels());
        }
        if (attributesInStrategyHasNextPage) {
            promises.push(fetchMoreAttributesInStrategy());
        }
        if (productMeasuresHasNextPage) {
            promises.push(fetchMoreProductMeasures());
        }
        try {
            yield Promise.all(promises);
            setPageNumber((prevPage) => prevPage + 1);
        }
        catch (error) {
            throw new Error(error.message);
        }
    }), [
        attributesInStrategyHasNextPage,
        hasNextPage,
        productLabelsHasNextPage,
        productMeasuresHasNextPage,
        fetchMoreAttributesInStrategy,
        fetchMoreProductAggregateLabels,
        fetchMoreProductMeasures,
    ]);
    const attributes = (0, react_1.useMemo)(() => {
        var _a, _b;
        if (attributesInStrategyLoading || productAggregateLabelsLoading || productMeasuresLoading) {
            return undefined;
        }
        if (!productAggregateLabelsData && !attributesInStrategyData) {
            return undefined;
        }
        const productAttributesList = (filterOptions === null || filterOptions === void 0 ? void 0 : filterOptions.includes(product_table_dynamic_columns_1.DynamicColumnsFilterOption.Original))
            ? (_a = productAggregateLabelsData === null || productAggregateLabelsData === void 0 ? void 0 : productAggregateLabelsData.map(({ attribute }) => ({
                id: attribute,
                label: attribute,
                isEditable: false,
            }))) !== null && _a !== void 0 ? _a : [] : [];
        const strategyAttributes = (filterOptions === null || filterOptions === void 0 ? void 0 : filterOptions.includes(product_table_dynamic_columns_1.DynamicColumnsFilterOption.Enriched))
            ? attributesInStrategyData !== null && attributesInStrategyData !== void 0 ? attributesInStrategyData : []
            : constants_1.BASE_PRODUCT_FIELDS.map((label) => ({ node: { label } }));
        const attributesInStrategyList = strategyAttributes.map(({ node: { label } }) => ({
            id: (0, table_columns_1.createColumnId)(label, { isAIEnriched: true }),
            label,
            isEditable: true,
        }));
        const productMeasuresList = (filterOptions === null || filterOptions === void 0 ? void 0 : filterOptions.includes(product_table_dynamic_columns_1.DynamicColumnsFilterOption.Measures))
            ? (_b = productMeasuresData === null || productMeasuresData === void 0 ? void 0 : productMeasuresData.map(({ node: { dimension } }) => ({
                id: (0, table_columns_1.createColumnId)(dimension, { isMeasure: true }),
                label: dimension,
                isEditable: false,
            }))) !== null && _b !== void 0 ? _b : [] : [];
        // Order is important
        const attributeList = productAttributesList.concat(attributesInStrategyList)
            .reduce((acc, item) => {
            const newAcc = Object.assign({}, acc);
            newAcc[item.label] = item;
            return newAcc;
        }, {});
        const sortedAttributes = [
            ...Array.from(Object.values(attributeList)),
            ...productMeasuresList,
        ].sort((a, b) => (0, utils_1.compareTextCaseInsensitive)(a.label, b.label));
        if (!hasNextPage) {
            return sortedAttributes;
        }
        const currentLastItemNumber = client_constants_1.REQUEST_LIMIT_MEDIUM * pageNumber;
        return sortedAttributes.slice(0, currentLastItemNumber);
    }, [
        attributesInStrategyLoading,
        productAggregateLabelsLoading,
        productMeasuresLoading,
        productAggregateLabelsData,
        attributesInStrategyData,
        filterOptions,
        productMeasuresData,
        hasNextPage,
        pageNumber,
    ]);
    return {
        isLoading: attributesInStrategyLoading || productAggregateLabelsLoading || productMeasuresLoading,
        pagination: {
            hasNextPage,
            isLoadingMore: attributesInStrategyMoreItemsLoading || productAggregateLabelsMoreItemsLoading || productMeasuresMoreItemsLoading,
            onNextPage: onLoadMore,
        },
        error: attributesInStrategyError || productAggregateLabelsError || productMeasuresError,
        attributes: attributes !== null && attributes !== void 0 ? attributes : [],
    };
};
exports.useGetAttributesAsColumns = useGetAttributesAsColumns;
exports.default = exports.useGetAttributesAsColumns;
